// Forked into ../redesign/Results/GroupGuidelines.tsx — keep in sync until phase_0 cleanup.
import React from 'react';

import ParseHtml from 'html-react-parser';
import { IconFile } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import type { Guideline } from '../types';
import { iconSizeExtraLarge } from '../../../helpers/iconSizes';
import {
	ClassicItem,
	ClassicNavigableHeadingButton,
	StyledImgWrapper,
	StyledInfoDiv,
	StyledItemsUL,
	StyledResultsSection,
	StyledViewAll,
} from './StyledComponents';
import { navigateTo } from '../../../helpers/windowLocation';
import { getSpotlightResultClassName } from '../helpers/getSpotlightResultClassName';
import { GUIDELINE_TITLE, parseHighlightedText } from '../helpers/highlightedText';
import { HighlightedText } from '../redesign/HighlightedText';
import { useSearch } from '../SpotlightState';

interface Props {
	guidelines: Guideline[];
}

export function GroupGuidelines({ guidelines }: Props) {
	const { searchTerm } = useSearch();
	const { translate } = useTranslate();

	const goToGuidelinesSearchPage = () => {
		navigateTo(
			`/guidelines/search?key=${encodeURIComponent(
				searchTerm,
			)}&type=all&from=1970-01-01T00:00:00.000Z&page=0`,
		);
	};

	return (
		<StyledResultsSection>
			<ClassicNavigableHeadingButton onSelect={goToGuidelinesSearchPage}>
				<div>{translate('spotlight.dropdown.guidelines')}</div>
				<StyledViewAll>
					<span>{translate('spotlight.dropdown.viewAll')}</span>
				</StyledViewAll>
			</ClassicNavigableHeadingButton>

			<StyledItemsUL>
				{guidelines.map((guideline: Guideline) => (
					<ClassicItem
						dataId={guideline.id}
						onSelect={() => navigateTo(guideline.path)}
						className={getSpotlightResultClassName('Guideline')}
						key={guideline.id}
					>
						<StyledImgWrapper>
							{guideline.iconSvg ? (
								// We trust guideline.iconSvg to be safe: it is one of guidelines-search-svc's hardcoded icons, not user-generated data.
								ParseHtml(guideline.iconSvg)
							) : (
								<IconFile size={iconSizeExtraLarge} />
							)}
						</StyledImgWrapper>
						<StyledInfoDiv>
							<div>
								<HighlightedText
									segments={parseHighlightedText(
										guideline.title,
										GUIDELINE_TITLE,
									)}
								/>
							</div>
						</StyledInfoDiv>
					</ClassicItem>
				))}
			</StyledItemsUL>
		</StyledResultsSection>
	);
}
